import * as _ from 'lodash'
import InfiniteScrollStore from '@/store/modules/InfiniteScrollStore'
import MessageList from '@/api/message-list'

interface State {
  allUnreadCount: number
  currentEntryKey: number
  entries: any[]
  hasMessageBadge: boolean
}

interface SortMessagesOption {
  data: any
  targetIndex: number
}

const defaultState = Object.assign(
  {
    allUnreadCount: 0,
    currentEntryKey: -1,
    entries: [],
    hasMessageBadge: false
  },
  InfiniteScrollStore.defaultState
)

const state: State = Object.assign({}, defaultState)

const mutations = Object.assign(
  {
    resetState(state: State) {
      const s = defaultState
      Object.keys(s).forEach(key => {
        state[key] = s[key]
      })
    },

    setAllUnreadCount(state: State, count: number) {
      state.allUnreadCount = count
    },

    setCurrentEntryKey(state: State, currentEntryKey: number) {
      state.currentEntryKey = currentEntryKey
    },

    /**
     * バッジの表示フラグをセット
     */
    setHasMessageBadge(state: State, hasMessageBadge: boolean) {
      state.hasMessageBadge = hasMessageBadge
    },

    /**
     * 未読件数をクリア
     */
    clearUnreadCount(state: State, key: string) {
      if (state.entries[key]) {
        state.entries[key].unread_count = 0
      }
    },

    /**
     * entry の未読件数を変更
     */
    setUnreadCountOfEntry(state: State, payload: { index: number; unread_count: number }) {
      if (state.entries[payload.index]) {
        state.entries[payload.index].unread_count = payload.unread_count
      }
    },

    /**
     * 未読件数をインクリメント
     */
    incrementUnreadCount(state: State, index: number) {
      state.entries[index].unread_count++
    }
  },
  InfiniteScrollStore.mutations
)

const actions = Object.assign(
  {
    /**
     * 特定ユーザからの未読件数を取得する
     */
    async getUnreadCountMessage({ commit }, payload: { index: number; id: string }) {
      try {
        const data = await MessageList.getUnreadCountMessage({
          id: payload.id
        })
        if (typeof payload.index === 'number') {
          commit('setUnreadCountOfEntry', {
            index: payload.index,
            unread_count: data.unread_count
          })
        }
      } catch (err) {
        console.error(err)
      }
    },

    /**
     * 未読のメッセージがあるかチェックする
     */
    async checkHasUnreadMessage({ commit }) {
      try {
        const data = await MessageList.checkHasUnreadMessage()
        let allUnreadCount = data.all_unread_count
        if (typeof allUnreadCount === 'undefined') return
        if (typeof allUnreadCount === 'string') {
          allUnreadCount = Number(allUnreadCount)
        }
        commit('setHasMessageBadge', allUnreadCount > 0)
        commit('setAllUnreadCount', allUnreadCount)
      } catch (err) {
        console.error(err)
      }
    },

    /**
     * 新着メッセージが先頭に来るようにソートする
     */
    sortMessages({ state, commit }, { data, targetIndex }: SortMessagesOption) {
      if (targetIndex < 0) return

      const timestamp = Object.keys(data.entries)[0]
      const entry = data.entries[timestamp]
      const isMissedCall = data.entries.missed_call
      const newData = _.get(entry, 'message') || _.get(entry, 'gift') || _.get(entry, 'stamp')

      // 古いデータを削除して新しいデータで更新
      const tmpEntry = Object.assign({}, state.entries[targetIndex])
      commit('removeEntry', targetIndex)

      // message_id を更新しないと2件目以降の新着メッセージがソートやカウントアップされない
      tmpEntry.message_id = data.message_id

      if (isMissedCall) {
        tmpEntry.message = entry.text
        tmpEntry.time = entry.time
      } else if (newData) {
        if (newData['text']) {
          tmpEntry.message = newData['text']
        } else {
          // テキスト以外をプッシュ通知で受信すると "○○さんから〜〜〜" の形式で送られて復るので不要な部分を削除
          const message = data.message.replace(/.+さんから/, '')
          tmpEntry.message = message
        }
        tmpEntry.time = newData['time']
      }
      tmpEntry.is_tel = isMissedCall
      commit('unshiftEntry', tmpEntry)
    },

    /**
     * 選択したユーザとの全てのメッセージを既読に
     */
    alreadyReadSelectedUsers({}, payload: any): Promise<any> {
      return MessageList.alreadyReadSelectedUsers(payload)
    },

    /**
     * 全ての未読メッセージを既読に
     */
    allAlreadyReadSelectedUsers({}, payload?: any): Promise<any> {
      return MessageList.allAlreadyReadSelectedUsers(payload)
    },

    /**
     * 選択したユーザとの全てのメッセージを既読に
     */
    removeSelected({}, payload: any): Promise<any> {
      return MessageList.removeSelected(payload)
    },

    /**
     * 選択したユーザとの全てのメッセージを既読に
     */
    allDelete({}, payload: any): Promise<any> {
      return MessageList.allDelete(payload)
    }
  },
  InfiniteScrollStore.actions
)

const getters = Object.assign(
  {
    allUnreadCount: (state: State): number => state.allUnreadCount,
    currentEntryKey: (state: State): number => state.currentEntryKey,
    hasMessageBadge: (state: State): boolean => state.hasMessageBadge
  },
  InfiniteScrollStore.getters
)

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}
